/**
 * Lightweight GFM subset for right-rail panels: headings, tables, lists, code, links.
 */

import React from 'react';

const TABLE_SEP_RE = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;

export function splitTableRow(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed.includes('|')) return [];
  const inner = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((cell) => cell.trim());
}

export function parseGfmTable(lines) {
  const rows = (lines || []).map((l) => String(l).trim()).filter((l) => l.includes('|'));
  if (rows.length < 2) return null;
  const header = splitTableRow(rows[0]);
  if (!header.length || !TABLE_SEP_RE.test(rows[1])) return null;
  const body = rows.slice(2).map(splitTableRow).filter((r) => r.length);
  return { header, body };
}

function renderInline(text) {
  const src = String(text || '');
  if (!src) return [''];

  const parts = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let match = re.exec(src);
  while (match) {
    if (match.index > last) parts.push(src.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`${match.index}-b`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      parts.push(
        <code
          key={`${match.index}-c`}
          className="rounded border border-cp-line bg-cp-surface px-1 py-0.5 font-mono text-[0.85em]"
        >
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('[')) {
      const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      if (m) {
        parts.push(
          <a
            key={`${match.index}-a`}
            href={m[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-cp-indigo underline underline-offset-2 hover:text-cp-violet"
          >
            {m[1]}
          </a>
        );
      } else {
        parts.push(token);
      }
    }
    last = match.index + token.length;
    match = re.exec(src);
  }
  if (last < src.length) parts.push(src.slice(last));
  return parts.length ? parts : [''];
}

function TableBlock({ header, body }) {
  return (
    <div className="my-3 w-full overflow-x-auto rounded-lg border border-cp-line">
      <table className="w-full min-w-[16rem] border-collapse text-left text-sm">
        <thead className="bg-cp-surface">
          <tr>
            {header.map((cell, i) => (
              <th
                key={`h-${i}`}
                className="border-b border-cp-line px-3 py-2 font-semibold text-cp-fg whitespace-nowrap"
              >
                {renderInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-cp-line">
          {body.map((row, ri) => (
            <tr key={`r-${ri}`} className="even:bg-cp-inset/40">
              {header.map((_, ci) => (
                <td
                  key={`c-${ri}-${ci}`}
                  className="border-b border-cp-line/80 px-3 py-2 align-top text-cp-fg break-words"
                >
                  {renderInline(row[ci] || '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isTableLine(line) {
  return String(line || '').trim().includes('|');
}

/** Split a paragraph block that may mix headings, tables, and prose. */
function segmentLines(lines) {
  const segments = [];
  let i = 0;
  const src = lines.map((l) => String(l));

  while (i < src.length) {
    if (isTableLine(src[i])) {
      const tableLines = [];
      while (i < src.length && isTableLine(src[i])) {
        tableLines.push(src[i]);
        i += 1;
      }
      segments.push({ kind: 'table', lines: tableLines });
      continue;
    }
    const chunk = [];
    while (i < src.length && !isTableLine(src[i])) {
      chunk.push(src[i]);
      i += 1;
    }
    if (chunk.length) segments.push({ kind: 'text', lines: chunk });
  }
  return segments;
}

function blockFromLines(lines) {
  const trimmed = lines.map((l) => String(l)).join('\n').trim();
  if (!trimmed) return null;

  const table = parseGfmTable(lines);
  if (table) {
    return <TableBlock key={trimmed.slice(0, 24)} header={table.header} body={table.body} />;
  }

  const first = trimmed.split('\n')[0];
  if (/^#{1,3}\s/.test(first) && !trimmed.includes('\n|')) {
    const level = first.match(/^#+/)[0].length;
    const text = first.replace(/^#+\s*/, '');
    const cls =
      level === 1
        ? 'mb-3 mt-1 text-lg font-semibold text-cp-fg'
        : level === 2
          ? 'mb-2 mt-4 text-base font-semibold text-cp-fg'
          : 'mb-2 mt-3 text-sm font-semibold text-cp-fg';
    const Tag = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
    return (
      <Tag key={trimmed.slice(0, 24)} className={cls}>
        {renderInline(text)}
      </Tag>
    );
  }

  if (/^```/.test(trimmed)) {
    const code = trimmed.replace(/^```[\w-]*\n?/, '').replace(/\n?```$/, '');
    return (
      <pre
        key={code.slice(0, 24)}
        className="mb-3 overflow-x-auto rounded-lg border border-cp-line bg-cp-console p-3 font-mono text-xs leading-relaxed text-[#e8edf7]"
      >
        {code}
      </pre>
    );
  }

  const listLines = lines.filter((l) => /^\s*[-*]\s+/.test(l) || /^\s*\d+\.\s+/.test(l));
  if (listLines.length === lines.length && listLines.length > 0) {
    const ordered = /^\s*\d+\.\s+/.test(listLines[0]);
    const Tag = ordered ? 'ol' : 'ul';
    const listClass = ordered
      ? 'mb-2 list-decimal space-y-1 pl-5'
      : 'mb-2 list-disc space-y-1 pl-5';
    return (
      <Tag key={trimmed.slice(0, 24)} className={listClass}>
        {listLines.map((line, i) => {
          const item = line.replace(/^\s*([-*]|\d+\.)\s+/, '');
          return (
            <li key={i} className="leading-relaxed">
              {renderInline(item)}
            </li>
          );
        })}
      </Tag>
    );
  }

  return (
    <p key={trimmed.slice(0, 24)} className="mb-2 whitespace-pre-wrap break-words leading-relaxed last:mb-0">
      {renderInline(trimmed)}
    </p>
  );
}

/** @returns {import('react').ReactNode[]} */
export function renderGfmPanelMarkdown(content) {
  const text = String(content || '').trim();
  if (!text) return [];

  const blocks = text.split(/\n{2,}/);
  const nodes = [];
  let keyIdx = 0;
  for (const block of blocks) {
    const lines = block.split('\n');
    for (const segment of segmentLines(lines)) {
      const node = blockFromLines(segment.lines);
      if (node) {
        nodes.push(React.cloneElement(node, { key: `md-${keyIdx++}` }));
      }
    }
  }
  return nodes;
}
